import { useCallback, useEffect } from 'react';
import { useValueRef } from './useValueRef';

type Props = {
  onZoom: (delta: number) => void;
  /** Enabled by default */
  enabled?: boolean;
  scrollAreaRef: HTMLDivElement | null;
  /** What keyboard key should be pressed in combination with wheel scroll? */
  comboKey?: keyof WheelEvent;
};

/**
 * Detect mousewheel + modifier key to zoom in/out on
 * @param options
 */
export const useZoom = (options: Props) => {
  const { onZoom, enabled = true, scrollAreaRef, comboKey = 'metaKey' } = options;
  const onZoomRef = useValueRef(onZoom);

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!enabled || !scrollAreaRef) return;

      if (e[comboKey]) {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 1 : -1;
        onZoomRef.current(delta);
      }
    },
    [scrollAreaRef, enabled, onZoomRef, comboKey]
  );

  useEffect(() => {
    if (scrollAreaRef) {
      scrollAreaRef.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        scrollAreaRef.removeEventListener('wheel', handleWheel);
      };
    }
  }, [scrollAreaRef, handleWheel]);
};
